// ============================================================
// BLINKBLIS - CENTRO DE CONTROL DE OFERTAS
// ============================================================
// Este es el UNICO archivo que tocas para cambiar links de afiliado
// o el contenido de una oferta.
//
// TODO el contenido es bilingue: cada campo lleva { es, en }.
//
// REGLA DE DATOS: cada cifra aqui salio de la pagina de la marca o de
// su ficha publica, no de una estimacion. Si una cifra cambia y no la
// puedes verificar, se borra: no se aproxima.
// Datos de Kovo verificados el 19 de septiembre de 2026.
//
// REGLA DE LENGUAJE (cumplimiento):
// Prohibido en toda la pagina: "sube tu puntaje", cifras de puntos,
// plazos, "aprobacion garantizada", "repara credito", testimonios.
// Permitido: "construir historial reportado", "puede ayudar",
// "sin revision de credito", "reporta a los buros".
// ============================================================

import { attributionTag } from './attribution'

export type Lang = 'es' | 'en'
export type L<T> = Record<Lang, T>

export const pick = <T,>(value: L<T>, lang: Lang): T =>
  (value && value[lang]) ?? value.es

export type Offer = {
  slug: string // slug en espanol -> /oferta/<slug>
  enSlug: string // slug en ingles -> /offer/<enSlug>
  brand: string
  affiliateUrl: string // link crudo de impact, SIN subId (se agrega solo)
  network: string
  confidence: number
  updated: L<string>
  readMinutes: number
  category: L<string>
  title: L<string>
  subtitle: L<string>
  ctaLabel: L<string>
  ctaMicro: L<string> // linea corta bajo cada boton
  ctaDisclaimer: L<string> // parrafo de cumplimiento junto al CTA principal
  facts: L<{ label: string; value: string }[]>
  glanceBestFor: L<string>
  glanceNotFor: L<string>
  bestFor: L<string[]>
  howItWorks: L<{ t: string; d: string }[]>
  pros: L<string[]>
  cons: L<string[]>
  notFor: L<string[]>
  faq: L<{ q: string; a: string }[]>
}

// ============================================================
// SUB ID
// ------------------------------------------------------------
// NO metas subIds en el formulario Advanced de impact: quedan fijos
// para siempre y no te dicen que anuncio convirtio.
//
// En Meta Ads:
//   Website URL      -> https://blinkblis.com/oferta/credito   (ES)
//                       https://blinkblis.com/offer/credit      (EN)
//   URL parameters   -> utm_source={{site_source_name}}
//                       &utm_medium=paid_social
//                       &utm_campaign={{campaign.name}}
//                       &utm_content={{ad.name}}
//                       &campaign_id={{campaign.id}}
//                       &adset_id={{adset.id}}
//                       &ad_id={{ad.id}}
//
// attributionTag() lee esos parametros, los recuerda durante toda la
// sesion y arma el subId en el momento del click de salida.
// ============================================================
export const withSubId = (url: string, slug: string) => {
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}subId1=${encodeURIComponent(attributionTag(slug))}`
}

export const OFFERS: Offer[] = [
  {
    slug: 'credito',
    enSlug: 'credit',
    brand: 'Kovo',
    affiliateUrl: 'https://kovo-credit.sjv.io/enZ41Q',
    network: 'impact.com',
    confidence: 88,
    readMinutes: 6,
    updated: { es: 'Septiembre 2026', en: 'September 2026' },
    category: { es: 'Credito', en: 'Credit' },
    title: {
      es: 'Sin historial de credito en Estados Unidos?',
      en: 'No credit history in the U.S.?',
    },
    subtitle: {
      es: 'Empieza a generar actividad de credito reportada por $10 al mes con Kovo. Sin revision de credito, sin deposito, y con los pagos reportados a Experian, Equifax, TransUnion e Innovis.',
      en: 'Start building reported credit activity for $10/month with Kovo. No credit check, no deposit, and payment activity reported to Experian, Equifax, TransUnion and Innovis.',
    },
    ctaLabel: {
      es: 'Empezar con Kovo',
      en: 'Start with Kovo',
    },
    ctaMicro: {
      es: 'Enlace de afiliado. Blinkblis puede recibir una comision. Tu precio no cambia.',
      en: 'Affiliate link. Blinkblis may earn a commission. Your price does not change.',
    },
    ctaDisclaimer: {
      es: 'Los resultados varian. Los pagos a tiempo pueden ayudar a tu perfil de credito. Los pagos tardios o no realizados pueden perjudicarlo. Blinkblis puede recibir una comision si abres una cuenta a traves de nuestro enlace.',
      en: 'Results vary. On-time payments may help your credit profile. Late or missed payments may hurt your credit. Blinkblis may earn a commission if you open an account through our link.',
    },
    facts: {
      es: [
        { label: 'Costo', value: '$10 al mes por 24 meses ($240 en total)' },
        { label: 'Intereses', value: '0%. Sin cargos ocultos' },
        { label: 'Revision de credito', value: 'No' },
        { label: 'Deposito inicial', value: 'No requiere' },
        {
          label: 'Reporta a',
          value: 'Experian, Equifax, TransUnion e Innovis',
        },
        {
          label: 'Incluye',
          value: 'Seguimiento de FICO y monitoreo de credito',
        },
        { label: 'Si te arrepientes', value: 'Reembolso dentro de los 30 dias' },
      ],
      en: [
        { label: 'Cost', value: '$10 a month for 24 months ($240 total)' },
        { label: 'Interest', value: '0%. No hidden fees' },
        { label: 'Credit check', value: 'No' },
        { label: 'Upfront deposit', value: 'Not required' },
        {
          label: 'Reports to',
          value: 'Experian, Equifax, TransUnion and Innovis',
        },
        {
          label: 'Includes',
          value: 'FICO score tracking and credit monitoring',
        },
        { label: 'If you change your mind', value: 'Refund within 30 days' },
      ],
    },
    glanceBestFor: {
      es: 'Personas que empiezan su historial de credito desde cero',
      en: 'People starting credit history from zero',
    },
    glanceNotFor: {
      es: 'Quien necesita efectivo, reparacion de credito o un aumento de puntaje garantizado',
      en: 'People needing cash, credit repair, or guaranteed score increases',
    },
    bestFor: {
      es: [
        'Llegaste hace poco a Estados Unidos',
        'No tienes historial de credito',
        'No logras que te aprueben una tarjeta de credito inicial',
        'Quieres que tu actividad de pago se reporte a los buros de credito',
        'Puedes sostener $10 al mes durante 24 meses',
      ],
      en: [
        'You are new to the United States',
        'You have no credit history',
        'You cannot get approved for a starter credit card',
        'You want payment activity reported to credit bureaus',
        'You can afford $10/month for 24 months',
      ],
    },
    howItWorks: {
      es: [
        {
          t: 'Abres tu cuenta en Kovo',
          d: 'Sin revision de credito y sin deposito. El proceso es en linea y toma unos minutos.',
        },
        {
          t: 'Pagas $10 al mes',
          d: 'Tu actividad de pago se reporta mensualmente a los cuatro buros.',
        },
        {
          t: 'Se acumula historial de pagos reportado',
          d: 'Los pagos a tiempo pueden ayudar a tu perfil de credito con el tiempo. Los resultados varian.',
        },
      ],
      en: [
        {
          t: 'Open your Kovo account',
          d: 'No credit check or deposit required. The process is online and takes a few minutes.',
        },
        {
          t: 'Pay $10/month',
          d: 'Your payment activity is reported monthly to all four bureaus.',
        },
        {
          t: 'Build reported payment history over time',
          d: 'On-time payments may help your credit profile over time. Results vary.',
        },
      ],
    },
    pros: {
      es: [
        'Costo mensual bajo frente a muchas alternativas para construir credito',
        'Reporta a los cuatro buros, no a uno solo. La mayoria de los productos de esta categoria reportan a menos',
        'No requiere revision de credito ni deposito para abrir la cuenta',
        'Sin intereses: el costo total es fijo y conocido desde el inicio',
        'Pagos automaticos, sin depender de recordar la fecha cada mes',
        'Incluye seguimiento de FICO y monitoreo de credito sin costo adicional',
      ],
      en: [
        'Low monthly cost compared with many credit-building alternatives',
        'Reports to all four bureaus, not just one. Most products in this category report to fewer',
        'No credit check and no deposit required to open the account',
        'No interest: the total cost is fixed and known from the start',
        'Automatic payments, so the due date does not depend on memory',
        'FICO score tracking and credit monitoring included at no extra cost',
      ],
    },
    cons: {
      es: [
        'Es un compromiso de 24 meses. El historial no se compra, se acumula',
        'No es un prestamo: al final del plazo no recibes efectivo',
        'Un pago tardio aqui puede perjudicar tu credito igual que en cualquier otra cuenta',
        'No repara credito danado ni elimina cuentas en colecciones',
        'La atencion al cliente es limitada segun resenas publicas de terceros',
      ],
      en: [
        'It is a 24-month commitment. History is not bought, it accumulates',
        'It is not a loan: you do not receive cash at the end of the term',
        'A late payment here may hurt your credit just like any other account',
        'It does not repair damaged credit or remove collections',
        'Customer service is limited according to public third-party reviews',
      ],
    },
    notFor: {
      es: [
        'Ya tienes un historial de credito solido',
        'Tienes cuentas en colecciones que conviene atender primero',
        'Necesitas dinero de emergencia',
        'Esperas resultados en unas pocas semanas',
        'No puedes sostener comodamente $10 al mes durante 24 meses',
      ],
      en: [
        'You already have solid credit history',
        'You have active collections that need attention first',
        'You need emergency money',
        'You expect results in a few weeks',
        'You cannot comfortably afford $10/month for 24 months',
      ],
    },
    faq: {
      es: [
        {
          q: 'Que recibo exactamente por mis $10 al mes?',
          a: 'Es importante que quede claro: no es un prestamo y no recibes efectivo. Obtienes acceso a una cuenta de credito a plazos cuyos pagos se reportan a los buros, mas seguimiento de tu FICO y monitoreo de credito. Lo que compras es el reporte, no el dinero.',
        },
        {
          q: 'De verdad no hay revision de credito?',
          a: 'Kovo no realiza revision de credito para abrir la cuenta. Si solicita datos basicos de identidad e ingreso para verificarte, pero eso no es una consulta a tu reporte de credito.',
        },
        {
          q: 'Cuanto tarda en verse reflejado?',
          a: 'Los buros actualizan en ciclos tipicamente mensuales, y el historial se construye con el tiempo acumulado de pagos reportados. No existe un plazo garantizado y varia segun cada situacion. Cualquiera que prometa una cifra de puntos en una fecha esta ofreciendo algo que no puede entregar.',
        },
        {
          q: 'Esto elimina la informacion negativa que ya tengo?',
          a: 'No. Ningun producto de credit builder elimina informacion negativa exacta y verificable de un reporte de credito. Construye historial nuevo; no borra el anterior.',
        },
        {
          q: 'Garantiza que me aprueben una tarjeta o un auto despues?',
          a: 'No. Ningun producto puede garantizar la aprobacion de otro, y el aumento de puntaje tampoco esta garantizado: los buros lo calculan con multiples factores. Lo que hace es generar historial de pagos reportado, uno de los factores que los emisores consideran.',
        },
        {
          q: 'Que pasa si cancelo a los dos meses?',
          a: 'Existe reembolso dentro de los primeros 30 dias. Pasado ese punto pierdes el beneficio principal, porque el historial se construye con pagos sostenidos: una cuenta abierta y cerrada rapido aporta muy poco.',
        },
        {
          q: 'Por que Blinkblis recomienda esto?',
          a: 'Porque para alguien que empieza desde cero cumple los cuatro criterios que evaluamos: entrada sin historial previo, costo bajo y fijo, reporte a los cuatro buros y terminos claros antes de entregar datos personales. Recibimos una comision si abres una cuenta, y eso esta declarado en cada pagina.',
        },
      ],
      en: [
        {
          q: 'What exactly do I get for my $10 a month?',
          a: 'This matters, so plainly: it is not a loan and you do not receive cash. You get access to an installment credit account whose payments are reported to the bureaus, plus FICO score tracking and credit monitoring. What you are paying for is the reporting, not the money.',
        },
        {
          q: 'Is there really no credit check?',
          a: 'Kovo does not run a credit check to open the account. It does collect basic identity and income information to verify you, but that is not an inquiry on your credit report.',
        },
        {
          q: 'How long until it shows up?',
          a: 'Bureaus typically update on monthly cycles, and history builds from accumulated reported payments over time. There is no guaranteed timeline and it varies by situation. Anyone promising a number of points by a date is offering something they cannot deliver.',
        },
        {
          q: 'Does this remove the negative marks I already have?',
          a: 'No. No credit builder product removes accurate, verifiable negative information from a credit report. It builds new history; it does not erase the old.',
        },
        {
          q: 'Does it guarantee I get approved for a card or a car later?',
          a: 'No. No product can guarantee approval for another, and a score increase is not guaranteed either: bureaus calculate scores from many factors. What it does is generate reported payment history, one of the factors lenders consider.',
        },
        {
          q: 'What if I cancel after two months?',
          a: 'There is a refund within the first 30 days. Past that point you lose the main benefit, because history is built through sustained payments: an account opened and closed quickly contributes very little.',
        },
        {
          q: 'Why does Blinkblis recommend this?',
          a: 'Because for someone starting from zero it meets the four criteria we evaluate: no prior history required, low and fixed cost, reporting to all four bureaus, and clear terms before you hand over personal data. We earn a commission if you open an account, and that is disclosed on every page.',
        },
      ],
    },
  },
]

// Resuelve una oferta por su slug en espanol o en ingles.
export const getOffer = (slug?: string) =>
  OFFERS.find((o) => o.slug === slug || o.enSlug === slug)

// Ruta canonica de una oferta segun el idioma.
export const offerPath = (offer: Offer, lang: Lang) =>
  lang === 'en' ? `/offer/${offer.enSlug}` : `/oferta/${offer.slug}`
